import { useState, useEffect, FormEvent } from 'react';
import { Form, Button, Row, Col, InputGroup, FormControl, Modal, Card, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faCheckCircle, faTrash, faPercentage, faEdit } from '@fortawesome/free-solid-svg-icons';
import { updateSharedPercentage } from '../../../services/Entreprise_service';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { createEntrepriseTeam, deleteEntrepriseTeam, getEntrepriseTeamByParentId } from '../../../services/entrepriseTeamService';
import { UserModel } from '../../../models/UserModel';

interface TeamProps {
    authUser: UserModel | null,
    fetchSecretary: () => Promise<any>,
    secretary: any,
    setSecretary: (secretary: any) => void,
}

const Team = ({ authUser, fetchSecretary, secretary, setSecretary }: TeamProps) => {
    const [newSecretary, setNewSecretary] = useState({
        civility: '',
        email: '',
        nom: '',
        prenom: '',
        sharedPercentageReferral: 0
    });

    const [showModal, setShowModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showPercentageModal, setShowPercentageModal] = useState(false);
    const [savingPercentage, setSavingPercentage] = useState(false);

    const userId = localStorage.getItem("userId");

    // Si fetchSecretary est fourni par le parent, il vaut mieux l'ajouter dans les deps.
    useEffect(() => {
        // on attend la promesse pour gérer d'éventuelles erreurs
        const load = async () => {
            try {
                await fetchSecretary();
            } catch (err) {
                console.error("Erreur fetchSecretary dans useEffect:", err);
            }
        };
        load();
    }, [fetchSecretary]);

    const handleChange = (e: any) => {
        const { id, value } = e.target;
        setNewSecretary(prev => ({
            ...prev,
            [id]: value,
        }));
    };

    const handlePercentageChange = (e: any) => {
        const value = Number(e.target.value);
        const maxAllowed = authUser?.referralPercentage ? Number(authUser.referralPercentage) * 0.99 : 99;

        if (value > maxAllowed) {
            toast.error(`Le pourcentage ne peut pas dépasser ${maxAllowed.toFixed(2)}%`);
            return;
        }

        setNewSecretary(prev => ({
            ...prev,
            sharedPercentageReferral: value
        }));
    };

    const handlePercentageModalChange = (e: any) => {
        const value = Number(e.target.value);
        const maxAllowed = authUser?.referralPercentage ? authUser.referralPercentage * 0.99 : 0;

        if (value > maxAllowed) {
            toast.error(`Le pourcentage ne peut pas dépasser ${maxAllowed.toFixed(2)}%`);
            return;
        }
        // on met à jour le secretary local (prop setter fournit par le parent)
        setSecretary({ ...secretary, sharedPercentageReferral: value });
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (
            !newSecretary.civility ||
            !newSecretary.email ||
            !newSecretary.nom ||
            !newSecretary.prenom ||
            !newSecretary.sharedPercentageReferral
        ) {
            await Swal.fire({
                icon: "warning",
                title: "Informations manquantes",
                text: "Merci de bien vouloir compléter tous les champs!",
                confirmButtonText: "OK",
            });
            return; // empêcher la suite si champs manquants
        } else {
            await submitSecretary(newSecretary);
            setShowModal(false);
        }
    };

    const submitSecretary = async (data: any) => {
        setSubmitting(true);

        try {
            const response = await createEntrepriseTeam(data);
            if (response?.data) {
                toast.success("L'assistant a été ajouté !");
                // Mettre à jour immédiatement l'UI
                setSecretary(response.data);
                // refetch si besoin pour synchroniser côté serveur / liste
                try { await fetchSecretary(); } catch (err) { console.warn("fetchSecretary après création échoué :", err); }

                // Reset form
                setNewSecretary({
                    civility: '',
                    email: '',
                    nom: '',
                    prenom: '',
                    sharedPercentageReferral: 0
                });
            }
        } catch (error: any) {
            console.error("Erreur lors de l'inscription :", error);
            const message = error?.response?.data?.message || "Une erreur inattendue est survenue";
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSavePercentage = async () => {
        setSavingPercentage(true);
        try {
            if (secretary && userId) {
                await updateSharedPercentage(secretary.id, secretary.sharedPercentageReferral);
                toast.success("Pourcentage mis à jour !");
                setShowPercentageModal(false);
                // Garder l'UI à jour (optionnel) :
                // await fetchSecretary();
            }
        } catch (error: any) {
            console.error("Erreur lors de la mise à jour du pourcentage :", error);
            const message = error?.response?.data?.message || "Une erreur inattendue est survenue";
            toast.error(message);
        } finally {
            setSavingPercentage(false);
        }
    };

    // Réécriture de la suppression en async/await + mise à jour optimiste de l'état
    const handleDeleteSecretary = async (id: any) => {
        const result = await Swal.fire({
            title: "VALIDER LA SUPPRESSION",
            text: "Êtes-vous sûr de vouloir supprimer cet assistant ?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#2A919D",
            cancelButtonColor: "#F4A261",
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
        });

        if (!result.isConfirmed) return;

        try {
            // appel API
            await deleteEntrepriseTeam(id);

            // mise à jour optimiste immédiate : retire l'assistant de l'UI
            setSecretary(null);

            // refetch pour resynchroniser si nécessaire (gestion des erreurs non bloquante)
            try { await fetchSecretary(); } catch (err) { console.warn("fetchSecretary après suppression échoué :", err); }

            await Swal.fire("Succès", `Suppression effectuée avec succès`, "success");
        } catch (error) {
            console.error("Erreur lors de la suppression de l'assistant :", error);
            toast.error("Une erreur inattendue est survenue");
        }
    };

    return (
        <div className="container mt-4">
            <Card className="p-3 shadow-sm">
                <Card.Body>
                    <Card.Title>Assistant de l'entreprise</Card.Title>
                    {
                        secretary
                            ?
                            <>
                                <p><strong>Nom :</strong> {secretary.prenom} {secretary.nom}</p>
                                <p><strong>Email :</strong> {secretary.email}</p>
                                <p><strong>RIB :</strong> {secretary.rib ? `${secretary.rib.slice(0, 4)}****${secretary.rib.slice(-4)}` : 'Non défini'}</p>
                                <p><strong>Solde :</strong> {secretary.solde?.toFixed(2) || '0.00'} €</p>
                                <p>
                                    <strong>Statut :</strong>{' '}
                                    <Badge bg="" style={{ background: '#2A919D' }}>
                                        <FontAwesomeIcon icon={faCheckCircle} /> Confirmé
                                    </Badge>
                                </p>
                                <p>
                                    <strong>Partage de Pourcentage :</strong> {secretary.sharedPercentageReferral}%
                                    <Button variant="" style={{ color: '#4BB0C0' }} size="sm" className="ms-2" onClick={() => setShowPercentageModal(true)}>
                                        <FontAwesomeIcon icon={faEdit} />
                                    </Button>
                                </p>
                                <Button variant="" style={{ background: '#F4A261', color: '#fff' }} size="sm" className="ms-2" onClick={() => handleDeleteSecretary(secretary.id)}>
                                    <FontAwesomeIcon icon={faTrash} /> Supprimer l'assistant
                                </Button>
                            </>
                            :
                            <p>Aucun assistant ajouté.</p>
                    }
                </Card.Body>
            </Card>

            {!secretary && (
                <Button variant="primary" className="mt-3" onClick={() => setShowModal(true)}>
                    Ajouter un Assistant
                </Button>
            )}

            {/* Modal for changing percentage */}
            <Modal show={showPercentageModal} onHide={() => setShowPercentageModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Modifier le Pourcentage</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Partage de Pourcentage (%) ( Max: {Number(authUser?.referralPercentage ? authUser?.referralPercentage : 0).toFixed(2)}) </Form.Label>
                        <InputGroup>
                            <FormControl
                                type="number"
                                min="0"
                                max={99.99}
                                step="0.01"
                                value={secretary?.sharedPercentageReferral ?? 0}
                                onChange={handlePercentageModalChange}
                                required
                            />
                            <InputGroup.Text><FontAwesomeIcon icon={faPercentage} /></InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">
                            Votre commission actuelle : {authUser?.referralPercentage}%. Le assistant peut recevoir jusqu'à {Number(authUser?.referralPercentage ? authUser?.referralPercentage * 0.99 : 0).toFixed(2)}%.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPercentageModal(false)}>Fermer</Button>
                    <Button variant="primary" onClick={handleSavePercentage} disabled={savingPercentage}>
                        {savingPercentage ? <Spinner animation="border" size="sm" /> : "Enregistrer"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal for adding secretary */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered
                style={{ zIndex: 1050 }}>
                <Modal.Header closeButton>
                    <Modal.Title>Ajouter un Assistant</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Row className="mb-3">
                            <Col md={12}>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <FontAwesomeIcon icon={faUser} />
                                    </InputGroup.Text>
                                    <FormControl
                                        as="select"
                                        id="civility"
                                        value={newSecretary.civility}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="" disabled>Civilité</option>
                                        <option value="monsieur">Monsieur</option>
                                        <option value="madame">Madame</option>
                                    </FormControl>
                                </InputGroup>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <FontAwesomeIcon icon={faUser} />
                                    </InputGroup.Text>
                                    <FormControl
                                        type="text"
                                        id="prenom"
                                        placeholder="Prénom"
                                        value={newSecretary.prenom}
                                        onChange={handleChange}
                                        required
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <FontAwesomeIcon icon={faUser} />
                                    </InputGroup.Text>
                                    <FormControl
                                        type="text"
                                        id="nom"
                                        placeholder="Nom"
                                        value={newSecretary.nom}
                                        onChange={handleChange}
                                        required
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <FontAwesomeIcon icon={faEnvelope} />
                                    </InputGroup.Text>
                                    <FormControl
                                        type="email"
                                        id="email"
                                        placeholder="Email"
                                        value={newSecretary.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Label>Partage de Pourcentage (%) ( Maximum de: {(Number(authUser?.referralPercentage) * 0.99).toFixed(2)})</Form.Label>
                                <InputGroup>
                                    <FormControl
                                        type="number"
                                        id="sharedPercentageReferral"
                                        min="0"
                                        max={100}
                                        step="0.01"
                                        value={newSecretary.sharedPercentageReferral}
                                        onChange={handlePercentageChange}
                                        required
                                    />
                                    <InputGroup.Text><FontAwesomeIcon icon={faPercentage} /></InputGroup.Text>
                                </InputGroup>
                                <Form.Text className="text-muted">
                                    Le RIB sera demandé lors de la soumission
                                </Form.Text>
                            </Col>
                        </Row>

                        <Button type="submit" variant="primary" className="mt-3 w-100" disabled={submitting}>
                            {submitting ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                'Ajouter Assistant'
                            )}
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Team;
